import { popup } from 'venuescanner-js/vanilla/components/popup';
// import { getToApi } from './api'
import io from 'socket.io-client';
import { $ } from 'venuescanner-js/tools/elemental';
import { ErrorDisplay } from 'venuescanner-js/vanilla/components/error';
import controllers from './controllers';
import { reportError } from 'venuescanner-js/react/services/errorService';
import { authService } from 'venuescanner-js/services/auth';

/** @module
 * @name inbox
 * @description Importable Socket Client for handling the Authentication, messages, responses and model listeners
 * @property {string} key The secret key generated for authenticating the User conenction
 * @property {string} url The URL of the Socket Server according to the requested Worker or Room
 * @property {io} socket The instance of the Socket.io connection
 * @property {boolean} authorisedUser If TRUE then the User has been authenticated
 * @property {string} room The name of the Room joined
 * @property {string} roomToJoin The name of the Room requested to join
 * @property {function} onJoined A function that is called once the Authentication is complete and the User has joined the requested Room
 * @property {Object} callbacks The store of the Promises to be resolved by responses, recorded by the GUID sent in the request
 * @property {Object[]} buffer A buffered array of the requested Promises to be run after the Client has joined a Room. This is only populated if the room has not been joined before the request is made.
 * @property {boolean} reconnected If TRUE then the Client has lost connection and had to reconnect.
 * @property {Object} _loaded The store of the Model names and IDs that the Socket Server should be aware that this client has loaded
 * @example
 * // connects to a specific room or worker
 * import {client} from 'venuescanner-js/services/socketClient'
 * const roomOrWorkerName = 'inbox'
 * client.init(roomOrWorkerName, () => {
 *   console.log('Joined')
 * })
 * @example
 * // connects to a specific room on a specific worker
 * import {client} from 'venuescanner-js/services/socketClient'
 * const roomName = 'inbox'
 * const workerName = 'inbox'
 * client.init(roomName, () => {
 *   console.log('Joined')
 * }, workerName)
 */

const client = {
  key: null,
  url: null,
  socket: null,
  authorisedUser: false,
  room: null,
  roomToJoin: 'calendar',
  onJoined: function () {},
  callbacks: {},
  buffer: [],
  reconnected: false,
  _loaded: null,

  /** @function
   * @name generateUUID
   * @description The generateUUID method is used to create the identifier used on pairing requests and responses. Sent in messages under the key **guid**.
   * @returns {string} The **guid** string
   */
  generateUUID: function () {
    let d = new Date().getTime();
    if (Date.now) {
      d = Date.now(); // high-precision timer
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
      },
    );
  },
  /** @function
   * @name init
   * @async
   * @description The initialising method that starts the socket connection to particular Room or Worker
   * @param {string} room The name of the Room or Worker (if worker is not also supplied) to join
   * @param {function=} onJoined The function called when the Client has joined the specified Room
   * @param {string=} worker The name of the Worker that the supplied Room is available on (only necessary if a Worker provides a number of Rooms, or the Room is provided by a Number of Workers)
   */
  init: async function (room, onJoined, worker) {
    if (room) this.roomToJoin = room;
    if (onJoined) this.onJoined = onJoined;
    this.worker = worker || room;
    popup.new('Connecting', { loading: true });
    if (this.key === null) {
      await this.getKey();
    }
    this.ready();
  },
  /** @function
   * @name getKey
   * @async
   * @description The method that discovers the Keys and Connection URL according to the provided Room/Worker<br />
   * *This is an internal method*
   */
  getKey: async function () {
    const { data } = await controllers.user.socketKeys.get({
      worker: this.worker,
    });

    this.key = data.socketKey;
    this.adminKey = data.adminSocketKey;
    this.url = data.socketUrl;
  },
  /** @function
   * @name ready
   * @description This method is called once the *init* method completes and the *getKey* method is successful.<br />
   * It will create the Socket and start the connection.<br />
   * *This is an internal method*
   */
  ready: function () {
    if (this.socket === null) {
      this.socket = io(this.url);
      this.socket.on('connect', this.connected.bind(this));
      this.socket.on('disconnect', this.disconnected.bind(this));
      this.socket.on('ready', this.identify.bind(this));
      this.socket.on('joined', this.joined.bind(this));
      this.socket.on('message', this.message.bind(this));
      this.socket.on('query', this.query.bind(this));
      this.socket.on('error', this.error('error'));
      this.socket.on('connect_error', this.error('connect_error'));
      this.socket.on('reconnect_error', this.error('reconnect_error'));
    }
  },
  /** @function
   * @name connected
   * @description This method is called by the Socket when a connection is established.<br />
   * *This is an internal method*
   */
  connected: function () {
    // console.log('Connected')
  },
  /** @function
   * @name disconnected
   * @description This method is called by the Socket when a connection is lost and the connection is attempting to reconnect.<br />
   * It will display a popup with a spinner, indicating this status. The popup will be removed on reconnection.<br />
   * *This is an internal method*
   */
  disconnected: function () {
    // console.log('Disconnected')
    this.room = null;
    popup.show('Reconnecting', { loading: true });
  },
  /** @function
   * @name identify
   * @description This method is called once a connection has been established and it will attempt Authentication.<br />
   * *This is an internal method*
   */
  identify: function () {
    this.socket.emit('auth', { key: this.key, adminKey: this.adminKey });
    // console.log('Identifying')
  },
  /** @function
   * @name authorised
   * @description This method is called once the User key has been authorised and will then attempt to join the requested Room.<br />
   * *This is an internal method*
   */
  authorised: function () {
    this.authorisedUser = true;
    // console.log('authorised')
    this.socket.emit('join', { room: this.roomToJoin });
    // console.log('Joining room')
  },
  /** @function
   * @name joined
   * @param {Object} data The response from the Socket Server on successful joining of a Room
   * @param {string} data.room The name of the Room that was successfully joined
   * @description This method is called once the User has successfully joined the requested room<br />
   * This will trigger the **onJoined** function (if supplied to **init**)<br />
   * If this a result of a reconnection, it will then trigger any reconnect listeners added.<br />
   * *This is an internal method*
   */
  joined: function (data) {
    // console.log(data.room)
    this.room = data.room;
    popup.close();
    this.runBuffer();
    this.onJoined();
    // console.log(this.socket);
    if (this._loaded) this.loaded(this._loaded);

    if (this.reconnected) {
      for (let m = this.listeners.reconnect.length - 1; m >= 0; m--) {
        try {
          this.listeners.reconnect[m]();
        } catch (e) {
          this.listeners.reconnect.splice(m, 1);
        }
      }
    }
    this.reconnected = true;
  },
  /** @function
   * @name message
   * @description This method is called by the Socket on receipt of a message. This handles each message type.<br />
   * *This is an internal method*
   * @param {Object} data The message data received from the Socket Server
   * @param {boolean} data.authorised If TRUE, then the **authorised** method is called, and nothing else.
   * @param {boolean=} data.warn If defined, then the **ErrorDisplay** module will show the content
   * @param {boolean=} data.info If defined, then the **ErrorDisplay** module will show the content
   * @param {string|Object=} data.error If defined, then the **ErrorDisplay** module will show the content and no other actions are performed
   * @param {boolean=} data.guid If defined and matches a request **guid**, then the request Promise is reolved with **data**
   * @param {Object=} data.any Any keys that match attached listeners, will trigger those listeners. Mainly used for observable model updating, implemented by the wrapping Service
   */
  message: function (data) {
    try {
      if (data) {
        // if (typeof(data)=="string") data=JSON.parse(data);
        if (data.authorised) return this.authorised();
        if (!this.room || !this.authorisedUser)
          throw new Error(
            'Must be authorised and joined a room before sending messages',
          );
        if (data.warn) ErrorDisplay.error(data.warn);
        if (data.info) ErrorDisplay.error(data.info);
        if (!!data.error && Object.keys(data.error).length > 0) {
          if (data.error === 'Not in a Room') {
            popup.show(
              $('<div></div>')
                .html(data.error)
                .append(
                  $('<center></center>').append(
                    $('<button></button>')
                      .attr('type', 'button')
                      .html('Reconnect?')
                      .click(function () {
                        window.location.reload();
                      }),
                  ),
                ),
              { closable: true },
            );
          } else {
            if (typeof data.error === 'string' || data.error.message) {
              ErrorDisplay.error(data.error.message || data.error);

              // console.log(data)
              if (data.guid && this.callbacks[data.guid]) {
                // console.log(data.error)
                this.callbacks[data.guid].reject(
                  new Error(data.error.message || data.error),
                );
                delete this.callbacks[data.guid];
              }
            } else {
              popup.show(
                $('<pre></pre>').html(JSON.stringify(data.error, null, 2)),
                { closable: true },
              );
            }
          }
        } else {
          Object.keys(data).forEach(k => {
            if (this.listeners.all[k]) {
              const op = {};
              op[k] = data[k];
              this.listeners.all[k].forEach(listener => {
                listener(op);
              });
            }
            if (!data.guid && this.listeners.update[k]) {
              const op = {};
              op[k] = data[k];
              this.listeners.update[k].forEach(listener => {
                listener(op);
              });
            }
          });
          if (data.trigger && this.listeners.trigger) {
            this.listeners.trigger.forEach(listener => {
              listener(data.trigger);
            });
          }
        }
        if (!!data.guid && !!this.callbacks[data.guid]) {
          this.callbacks[data.guid].resolve(data);
          delete this.callbacks[data.guid];
        } else if (!data.guid) {
          // Object.keys(data).forEach(k => {
          //   if (this.listeners.new[k]) {
          //     const op = {}
          //     op[k] = data[k]
          //     this.listeners.new[k].forEach(listener => {
          //       listener(op)
          //     })
          //   }
          // })
        }
      } else {
        console.warn('NULL data received over socket');
      }
    } catch (e) {
      console.error(e);
    }
  },
  /** @function
   * @name query
   * @description This method is called by the Socket on receipt of a query response. A query is requested in the same way a Room request, except, call **query** and not **room**.<br />
   * *This is an internal method*
   * @param {Object} data The message data received from the Socket Server
   * @param {boolean} data.authorised If TRUE, then the **authorised** method is called, and nothing else.
   * @param {boolean} data.guid If defined and matches a request **guid**, then the request Promise is reolved with **data**.
   * @param {Object} data.any Any additional keys contain the response data
   */
  query: function (data) {
    if (data) {
      // if (typeof(data)=="string") data=JSON.parse(data);
      if (data.authorised) return this.authorised();
      if (!this.room || !this.authorisedUser)
        throw new Error(
          'Must be authorised and joined a room before sending messages',
        );
      if (data.guid && this.callbacks[data.guid])
        this.callbacks[data.guid].resolve(data);
    } else {
      console.warn('NULL data received over socket');
    }
  },

  /** @function
   * @name update
   * @description This method is an abstraction of the **update** method in the room. This just removes the need to defined the request to the room.
   * @param {Object} data The update data to send to the room update method
   * @param {function=} cb The optional function for the response. If not defined, then it is purely regarding as a push of data.
   */
  update: function (data, cb) {
    this.send('room', { update: data }, cb);
  },

  /** @function
   * @name create
   * @description This method is an abstraction of the **create** method in the room. This just removes the need to defined the request to the room.
   * @param {Object} data The create data to send to the room create method
   * @param {function=} cb The optional function for the response. If not defined, then it is purely regarding as a push of data.
   */
  create: function (data, cb) {
    this.send('room', { create: data }, cb);
  },

  /** @function
   * @name sendPromise
   * @description This method emits the command with data, but returns a Promise.
   * @param {string} cmd The command to call on the Socket Server. If **room** then the key in the **data** object becomes the command to run within the Room
   * @param {Object} data The data to send to the Socket server
   * @returns {Object<Promise>} The response data
   */
  sendPromise: function (cmd, data) {
    return new Promise((resolve, reject) => {
      if (!data) data = {};
      data.guid = data.guid || this.generateUUID();
      this.callbacks[data.guid] = { resolve, reject };

      if (!this.room) {
        this.buffer.push([cmd, data]);
      } else {
        this.socket.emit(cmd, data);
      }
    });
  },

  /** @function
   * @name send
   * @description This method emits the command with data, but would handle a response with a callback function.
   * @param {string} cmd The command to call on the Socket Server. If **room** then the key in the **data** object becomes the command to run within the Room
   * @param {Object} data The data to send to the Socket server
   * @param {function=} cb The optional function for the response. If not defined, then it is purely regarding as a push of data.
   */
  send: function (cmd, data, cb) {
    if (!data) data = {};
    if (cb) {
      data.guid = this.generateUUID();
      // TODO: should be this.callbacks[data.guid] = { resolve: cbSucces, reject: (cbError ? cbError : () => {}) }
      this.callbacks[data.guid] = { resolve: cb, reject: () => {} };
    }
    if (!this.room) {
      this.buffer.push([cmd, data]);
    } else {
      this.socket.emit(cmd, data);
    }
  },

  /** @function
   * @name runBuffer
   * @description This method converts any **send** requests that have been buffered into actual sends. This is run once the Client has connected.
   */
  runBuffer: function () {
    while (this.buffer.length > 0) {
      this.send.apply(this, this.buffer.pop());
    }
  },
  listeners: {
    all: {},
    // new: {},
    update: {},
    reconnect: [],
  },

  /** @function
   * @name addListener
   * @description This method attaches a function to a specified listener, which would be triggered on incoming data containing the specified key.
   * @param {string} key The key in incoming data that would trigger this listener
   * @param {function} method The function that is called  when the specified key is in incoming data
   * @returns true
   */
  addListener: function (key, method) {
    if (key === 'trigger') {
      if (!this.listeners.trigger) this.listeners.trigger = [];
      this.listeners.trigger.push(method);
      return true;
    } else {
      if (!this.listeners.all[key]) this.listeners.all[key] = [];
      this.listeners.all[key].push(method);
      return true;
    }
  },

  /** @function
   * @name addUpdateListener
   * @description This method attaches a function to a specified listener, which would be triggered on incoming data containing the specified key, if it is regarded as an update.
   * @param {string} key The key in incoming data that would trigger this listener
   * @param {function} method The function that is called  when the specified key is in incoming data
   * @returns true
   */
  addUpdateListener: function (key, method) {
    if (!this.listeners.update[key]) this.listeners.update[key] = [];
    this.listeners.update[key].push(method);
    return true;
  },
  // addNewListener: function (key, method) {
  //   if (!this.listeners.new[key]) this.listeners.new[key] = []
  //   this.listeners.new[key].push(method)
  //   return true
  // },

  /** @function
   * @name onReconnect
   * @description This method attaches a function to the **reconnect** listener. This means the supplied function will be call every time the Socket is successfully reconnected.
   * @param {function} method The function that is called when the socket reconnects
   */
  onReconnect: function (method) {
    this.listeners.reconnect.push(method);
  },
  /** @function
   * @name removeListener
   * @description This method detaches a certain method from a certain key
   * @param {string} key The key in incoming data that would have triggered this listener
   * @param {function} method The function that was to be called when the specified key is in incoming data
   */
  removeListener: function (key, method) {
    Object.keys(this.listeners).forEach(type => {
      if (this.listeners[type][key] instanceof Array)
        this.listeners[type][key] = this.listeners[type][key].filter(
          m => m !== method,
        );
    });
  },
  /** @function
   * @name clearListeners
   * @description This method removes all listeners. Can be good practice to run this method on reconnections, to prevent multiple listeners for the same Models
   */
  clearListeners: function () {
    this.listeners = {
      all: {},
      // new: {},
      update: {},
      reconnect: [],
    };
  },
  /** @function
   * @name loaded
   * @description This method records in the client and informs the Socket Server, of the Model names and IDs that this User has loaded. This to be managed by the wrapping Service
   * @param {Object} loaded Object containing the Model names as keys, containing arrays of the Mongo IDs of those loaded model instances
   */
  loaded: function (loaded) {
    this._loaded = loaded;
    this.send('loaded', loaded);
  },
  /** @function
   * @name registerModels
   * @description This method applies a listener to a number of keys. Used primarily in Observable Model updating but can be used for other purposes.
   * @param {string[]} models The keys in incoming messages which would call the provided callback
   * @param {function} callback The function that is called when then provided keys (models) are in an incoming message
   */
  registerModels: function (models, callback) {
    models.forEach(model => {
      // console.log(`Registered ${model} with client`)
      this.addListener(model, callback);
    });
  },
  error(event) {
    return error => {
      const { user } = authService;

      const extra = {
        event,
        room: this.worker,
        ...(user?._id ? { 'user.id': user?._id.toString() } : {}),
      };

      reportError('socketClient', error, null, extra);
    };
  },
};
export { client };
export default client;
